import React, { useEffect, useState, useCallback } from 'react';
import './SuperadminApproval.css';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../api';
import GalleryViewer from '../components/GalleryViewer';
import AdminVideoManager from '../components/AdminVideoManager';

function SuperadminApproval() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reporters, setReporters] = useState([]);
  const [consentForms, setConsentForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('approvals'); // approvals | approved | public-approvals | public-approved | contacts | reporters | consent | videos
  const [subview, setSubview] = useState('ujala'); // 'ujala' | 'gallery' | 'events' for approvals
  
  // Document management states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedReporter, setSelectedReporter] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success', 'error', 'info'
  const [showToast, setShowToast] = useState(false);

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  const publicApiRequest = useCallback(async (method, path, data, config = {}) => {
    try {
      if (method === 'get') return await api.get(`/api/public-news${path}`, config);
      if (method === 'post') return await api.post(`/api/public-news${path}`, data, config);
      if (method === 'put') return await api.put(`/api/public-news${path}`, data, config);
      if (method === 'delete') return await api.delete(`/api/public-news${path}`, config);
    } catch (err) {
      if (err && err.response && err.response.status === 404) {
        if (method === 'get') return await api.get(`/api/news/public${path}`, config);
        if (method === 'post') return await api.post(`/api/news/public${path}`, data, config);
        if (method === 'put') return await api.put(`/api/news/public${path}`, data, config);
        if (method === 'delete') return await api.delete(`/api/news/public${path}`, config);
      }
      throw err;
    }
    throw new Error('Unsupported request method');
  }, []);

  // Guard: require a valid superadmin session
  useEffect(()=>{
    if (!token || role !== 'superadmin') {
      // clear any stale tokens/roles
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      navigate('/superadmin/login');
    }
  }, [token, role, navigate]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/superadmin/approval', { headers });
      if (res && res.data) {
        let data = res.data.data || [];
        // when viewing ujala approvals, exclude gallery/event items so they appear only in their subviews
        if (subview === 'ujala') data = data.filter(it => !it.isGallery && !it.isEvent);
        setItems(data);
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load pending items');
    }
    setLoading(false);
  }, [token, subview, navigate]);

  const fetchApproved = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/admin/approved-news', { headers });
      if (res && res.data) {
        let data = res.data.data || [];
        // show only non-gallery/non-event items in the approved 'ujala' list
        if (subview === 'ujala') data = data.filter(it => !it.isGallery && !it.isEvent);
        setItems(data);
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load approved news');
    }
    setLoading(false);
  }, [token, subview, navigate]);

  const fetchApprovedGallery = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/admin/approved-gallery', { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to load approved gallery');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchApprovedEvents = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/admin/approved-events', { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to load approved events');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchPendingGallery = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/superadmin/approval/gallery', { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to load gallery approvals');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchPendingEvents = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/news/superadmin/approval/events', { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to load event approvals');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchPublicPending = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await publicApiRequest('get', '/superadmin/pending', null, { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load public pending news');
    }
    setLoading(false);
  }, [token, navigate, publicApiRequest]);

  const fetchPublicApproved = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await publicApiRequest('get', '/superadmin/approved', null, { headers });
      if (res && res.data) setItems(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load public approved news');
    }
    setLoading(false);
  }, [token, navigate, publicApiRequest]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/contact', { headers });
      if (res && res.data) setContacts(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load contacts');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchReporters = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/users/reporters', { headers });
      if (res && res.data) setReporters(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load reporters');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchConsentForms = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/users/consent-forms', { headers });
      if (res && res.data) setConsentForms(res.data.data || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load consent forms');
    }
    setLoading(false);
  }, [token, navigate]);

  const fetchDocuments = async (reporterId) => {
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get(`/api/users/reporters/${reporterId}/documents`, { headers });
      if (res && res.data && res.data.success) {
        setDocuments(res.data.data.documents || []);
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to load documents');
    }
  };

  const uploadDocument = async (file) => {
    if (!selectedReporter) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.post(`/api/users/reporters/${selectedReporter._id}/documents`, formData, { 
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      if (res && res.data && res.data.success) {
        showNotification('Document uploaded successfully', 'success');
        await fetchDocuments(selectedReporter._id);
      } else {
        showNotification(res.data.message || 'Upload failed', 'error');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        showNotification(msg, 'error');
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      showNotification(err.message || 'Failed to upload document', 'error');
    }
    setUploadingDoc(false);
  };

  const deleteDocument = async (docId) => {
    if (!selectedReporter || !window.confirm('Delete this document?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.delete(`/api/users/reporters/${selectedReporter._id}/documents/${docId}`, { headers });
      
      if (res && res.data && res.data.success) {
        showNotification('Document deleted', 'success');
        await fetchDocuments(selectedReporter._id);
      } else {
        showNotification(res.data.message || 'Delete failed', 'error');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        showNotification(msg, 'error');
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      showNotification(err.message || 'Failed to delete document', 'error');
    }
  };
  
  useEffect(()=>{
    // When view/subview change, fetch the correct list.
    if (view === 'approvals') {
      if (subview === 'ujala') fetchPending();
      else if (subview === 'gallery') fetchPendingGallery();
      else if (subview === 'events') fetchPendingEvents();
    } else if (view === 'approved') {
      if (subview === 'ujala') fetchApproved();
      else if (subview === 'gallery') fetchApprovedGallery();
      else if (subview === 'events') fetchApprovedEvents();
    } else if (view === 'public-approvals') {
      fetchPublicPending();
    } else if (view === 'public-approved') {
      fetchPublicApproved();
    } else if (view === 'contacts') {
      fetchContacts();
    } else if (view === 'reporters') {
      fetchReporters();
    } else if (view === 'consent') {
      fetchConsentForms();
    }
  }, [view, subview, fetchPending, fetchPendingGallery, fetchPendingEvents, fetchApproved, fetchApprovedGallery, fetchApprovedEvents, fetchPublicPending, fetchPublicApproved, fetchContacts, fetchReporters, fetchConsentForms]);

  const approve = async (id) => {
    if (!window.confirm('Approve this item?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.put(`/api/news/superadmin/approval/${id}/approve`, null, { headers });
      if (res && res.data && res.data.success) {
        alert('Approved');
        fetchPending();
      } else alert((res && res.data && res.data.message) || 'Failed');
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to approve');
    }
  };

  const approveGallery = async (id) => {
    if (!window.confirm('Approve this gallery item?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.put(`/api/news/superadmin/approval/${id}/approve/gallery`, null, { headers });
      if (res && res.data && res.data.success) { alert('Gallery approved'); if (subview==='gallery') fetchPendingGallery(); else fetchPending(); } else alert((res && res.data && res.data.message) || 'Failed');
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to approve');
    }
  };

  const approveEvent = async (id) => {
    if (!window.confirm('Approve this event item?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.put(`/api/news/superadmin/approval/${id}/approve/event`, null, { headers });
      if (res && res.data && res.data.success) { alert('Event approved'); if (subview==='events') fetchPendingEvents(); else fetchPending(); } else alert((res && res.data && res.data.message) || 'Failed');
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) { const msg = (err.response.data && err.response.data.message) || 'Unauthorized'; alert(msg); localStorage.removeItem('token'); localStorage.removeItem('role'); return navigate('/superadmin/login'); }
      alert(err.message || 'Failed to approve');
    }
  };

  const approvePublic = async (id) => {
    if (!window.confirm('Approve this public news?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await publicApiRequest('put', `/superadmin/${id}/approve`, null, { headers });
      if (res && res.data && res.data.success) {
        alert('Public news approved');
        fetchPublicPending();
      } else {
        alert((res && res.data && res.data.message) || 'Failed');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to approve public news');
    }
  };

  const editPublic = async (item) => {
    const title = window.prompt('Edit title', item.title || '');
    if (title === null) return;
    const description = window.prompt('Edit description', item.description || '');
    if (description === null) return;
    const uploaderName = window.prompt('Edit uploader name', item.uploaderName || item.author || '');
    if (uploaderName === null) return;
    const mobileNumber = window.prompt('Edit mobile number', item.mobileNumber || '');
    if (mobileNumber === null) return;
    const adharCardNumber = window.prompt('Edit adhar card number', item.adharCardNumber || '');
    if (adharCardNumber === null) return;

    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await publicApiRequest('put', `/superadmin/${item._id}`, {
        title,
        description,
        uploaderName,
        mobileNumber,
        adharCardNumber,
      }, { headers });

      if (res && res.data && res.data.success) {
        alert('Public news updated');
        if (view === 'public-approvals') fetchPublicPending();
        if (view === 'public-approved') fetchPublicApproved();
      } else {
        alert((res && res.data && res.data.message) || 'Failed');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to edit public news');
    }
  };

  const deletePublic = async (id, approvedList = false) => {
    if (!window.confirm('Delete this public news item?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await publicApiRequest('delete', `/superadmin/${id}`, null, { headers });
      if (res && res.data && res.data.success) {
        alert('Deleted');
        if (approvedList) fetchPublicApproved();
        else fetchPublicPending();
        try { window.dispatchEvent(new Event('featured-updated')); localStorage.setItem('featured-updated', Date.now().toString()); } catch (e) {}
      } else {
        alert((res && res.data && res.data.message) || 'Failed');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to delete public news');
    }
  };

  const togglePublicHome = async (item) => {
    const enable = !item.isFeatured;
    if (!window.confirm(enable ? 'Set this public news to home?' : 'Remove this public news from home?')) return;
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const endpoint = enable ? `/superadmin/${item._id}/feature` : `/superadmin/${item._id}/unfeature`;
      const res = await publicApiRequest('put', endpoint, null, { headers });
      if (res && res.data && res.data.success) {
        alert(enable ? 'Marked as featured' : 'Removed from home');
        fetchPublicApproved();
        try { window.dispatchEvent(new Event('featured-updated')); localStorage.setItem('featured-updated', Date.now().toString()); } catch (e) {}
      } else {
        alert((res && res.data && res.data.message) || 'Failed');
      }
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
        alert(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/superadmin/login');
      }
      alert(err.message || 'Failed to update home status');
    }
  };

  

  const logout = () => { localStorage.removeItem('token'); localStorage.removeItem('role'); navigate('/'); };

  const makeSrc = (p) => {
    if (!p) return (API_BASE || '') + '/placeholder.png';
    if (/^https?:\/\//i.test(p)) return p;
    // p might be '/uploads/...' or 'uploads/...'
    const path = p.startsWith('/') ? p : '/' + p;
    return (API_BASE || '') + path;
  };
  const imgFor = (item) => {
    // prefer gallery first for gallery items
    if (item.isGallery && Array.isArray(item.galleryImages) && item.galleryImages.length > 0) return makeSrc(item.galleryImages[0]);
    return makeSrc(item.imageUrl || item.imagePath || '/placeholder.png');
  };

  const categoryBadge = (item) => {
    const raw = String(item?.category || '').trim();
    const lower = raw.toLowerCase();
    if (item?.isPublicSubmission || lower.includes('public')) return 'RR PUBLIC NEWS';
    if (item?.isGallery || lower.includes('gallery')) return 'RR NEWS TV GALLERY';
    if (item?.isEvent || lower.includes('event')) return 'RR NEWS TV EVENT';
    if (item?.isUjala || lower.includes('ujala') || lower.includes('moradabad')) return 'RR NEWS TV FASTEST UPDATE';
    return raw || 'RR NEWS TV FASTEST UPDATE';
  };

  return (
    <div className="container superadmin-approval">
      <div className="approval-header">
        <h2>Superadmin Panel</h2>
      </div>

      <div className="admin-panel">
        <aside className="admin-sidebar">
          <div className="admin-info">
            <div className="admin-avatar">SA</div>
            <div className="admin-meta">
              <div className="admin-name">Superadmin</div>
              <div className="admin-role">मुख्य व्यवस्थापक</div>
            </div>
          </div>

          <nav className="admin-nav">
            <button className={`sidebar-btn ${view==='approvals' && subview==='ujala'?'active':''}`} onClick={()=>{setView('approvals'); setSubview('ujala');}}>Approval Requests</button>
            <button className={`sidebar-btn ${view==='approvals' && subview==='gallery'?'active':''}`} onClick={()=>{setView('approvals'); setSubview('gallery');}}>Gallery Approvals</button>
            <button className={`sidebar-btn ${view==='approvals' && subview==='events'?'active':''}`} onClick={()=>{setView('approvals'); setSubview('events');}}>Event Approvals</button>
            <button className={`sidebar-btn ${view==='approved' && subview==='ujala'?'active':''}`} onClick={()=>{setView('approved'); setSubview('ujala');}}>Approved News</button>
            <button className={`sidebar-btn ${view==='approved' && subview==='gallery'?'active':''}`} onClick={()=>{setView('approved'); setSubview('gallery');}}>Approved Gallery</button>
            <button className={`sidebar-btn ${view==='approved' && subview==='events'?'active':''}`} onClick={()=>{setView('approved'); setSubview('events');}}>Approved Events</button>
            <button className={`sidebar-btn ${view==='public-approvals'?'active':''}`} onClick={()=>{setView('public-approvals');}}>Public Approvals</button>
            <button className={`sidebar-btn ${view==='public-approved'?'active':''}`} onClick={()=>{setView('public-approved');}}>Public Approved</button>
            <button className={`sidebar-btn ${view==='contacts'?'active':''}`} onClick={()=>{setView('contacts');}}>Contacts</button>
            <button className={`sidebar-btn ${view==='reporters'?'active':''}`} onClick={()=>{setView('reporters');}}>Reporters</button>
            <button className={`sidebar-btn ${view==='consent'?'active':''}`} onClick={()=>{setView('consent');}}>Consent Forms</button>
            <button className={`sidebar-btn ${view==='videos'?'active':''}`} onClick={()=>{setView('videos');}}>Video Manager</button>
          </nav>

          <div className="admin-logout">
            <button className="btn logout" onClick={logout}>Logout</button>
          </div>
        </aside>

        <main className="admin-content">
          {loading && <p className="muted">Loading...</p>}

          {view === 'approvals' && (
            <>
              <h3 className="section-title">{subview==='ujala' ? 'Pending RR NEWS Approvals' : subview==='gallery' ? 'Pending Gallery Approvals' : 'Pending Event Approvals'}</h3>
              {items.length === 0 && !loading && <p className="muted">No pending items</p>}
              <div className="approval-grid">
                {items.map(item => (
                  <div key={item._id} className="approval-card">
                    {item.isUjala && (
                      <div className="ujala-marquee css-marquee" role="region" aria-label="उजाला शीर्षक">
                        <div className="marquee-track">RR NEWS TV FASTEST UPDATE — खबर आपकी, नज़र हमारी</div>
                      </div>
                    )}

                    <div className="card-image">
                      {/* Render gallery carousel first for gallery items, then video/audio, then image */}
                      {item.isGallery && Array.isArray(item.galleryImages) && item.galleryImages.length > 0 ? (
                          <GalleryViewer images={item.galleryImages} alt={item.title} showThumbs={false} />
                      ) : item.videoUrl ? (
                        <MediaPreview src={makeSrc(item.videoUrl)} alt={item.title} imgFallback={imgFor(item)} />
                      ) : (
                        <img src={imgFor(item)} alt={item.title} />
                      )}
                      {item.isBreaking && <span className="breaking-badge">⚡ ब्रेकिंग न्यूज़</span>}
                    </div>

                    <div className="card-body">
                      <span className="news-category">{categoryBadge(item)}</span>
                      <div className="news-author">✍️ {item.author || '—'}</div>
                      <h3 className="news-title">{item.title}</h3>
                      <p className="news-description">{item.description}</p>

                      <div className="news-meta">
                        <span className="news-location">📍 {item.location || '—'}</span>
                        <span className="news-date">{new Date(item.createdAt).toLocaleString('hi-IN')}</span>
                      </div>

                      <div className="card-actions">
                        <button className="btn view" onClick={()=> navigate(`/news/${item.slug}`, { state: { news: item, fromApproval: true } })}>View</button>
                        {subview === 'gallery' ? (
                          <button className="btn approve" onClick={()=>approveGallery(item._id)}>Approve Gallery</button>
                        ) : subview === 'events' ? (
                          <button className="btn approve" onClick={()=>approveEvent(item._id)}>Approve Event</button>
                        ) : (
                          <button className="btn approve" onClick={()=>approve(item._id)}>Approve</button>
                        )}
                        <button className="btn danger" onClick={async ()=>{
                          if(!window.confirm('Delete this news item? This cannot be undone.')) return;
                          try{
                            const headers = token ? { Authorization: 'Bearer ' + token } : {};
                            const res = await api.delete(`/api/news/${item._id}`, { headers });
                            if (res && res.data && res.data.success){ alert('Deleted'); fetchPending(); } else {
                              alert((res && res.data && res.data.message) || 'Failed');
                            }
                          }catch(err){
                            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                              const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                              alert(msg);
                              localStorage.removeItem('token');
                              localStorage.removeItem('role');
                              return navigate('/superadmin/login');
                            }
                            alert(err.message);
                          }
                        }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {view === 'approved' && (
            <>
              <h3 className="section-title">Approved RR NEWS</h3>
              {items.length === 0 && !loading && <p className="muted">No approved items</p>}
              <div className="approval-grid">
                {items.map(item => (
                  <div key={item._id} className="approval-card">
                    {item.isUjala && (
                      <div className="ujala-marquee css-marquee" role="region" aria-label="उजाला शीर्षक">
                        <div className="marquee-track">RR NEWS TV FASTEST UPDATE — खबर आपकी, नज़र हमारी</div>
                      </div>
                    )}

                    <div className="card-image">
                      {item.isGallery && Array.isArray(item.galleryImages) && item.galleryImages.length > 0 ? (
                        <GalleryViewer images={item.galleryImages} alt={item.title} showThumbs={false} compact={true} />
                      ) : item.videoUrl ? (
                        <MediaPreview src={makeSrc(item.videoUrl)} alt={item.title} imgFallback={imgFor(item)} />
                      ) : (
                        <img src={imgFor(item)} alt={item.title} />
                      )}
                      {item.isBreaking && <span className="breaking-badge">⚡ ब्रेकिंग न्यूज़</span>}
                    </div>
                    <div className="card-body">
                      <span className="news-category">{categoryBadge(item)}</span>
                      <div className="news-author">✍️ {item.author || '—'}</div>
                      <h3 className="news-title">{item.title}</h3>
                      <p className="news-description">{item.description}</p>

                      <div className="news-meta">
                        <span className="news-location">📍 {item.location || '—'}</span>
                        <span className="news-date">{new Date(item.createdAt).toLocaleString('hi-IN')}</span>
                      </div>

                      <div className="card-actions">
                        <button className="btn view" onClick={()=> navigate(`/news/${item.slug}`, { state: { news: item, fromApproval: false } })}>View</button>
                        <button className="btn approve" onClick={()=> navigate('/admin/upload', { state: { edit: true, news: item } })}>Edit</button>
                        {item.isFeatured ? (
                          <button className="btn" onClick={async ()=>{
                            if(!window.confirm('Remove this news from home featured?')) return;
                            try{
                              const headers = token ? { Authorization: 'Bearer ' + token } : {};
                              const res = await api.put(`/api/news/admin/approved-news/${item._id}/unfeature`, null, { headers });
                              if (res && res.data && res.data.success) { alert('Removed from home'); fetchApproved(); try{ window.dispatchEvent(new Event('featured-updated')); localStorage.setItem('featured-updated', Date.now().toString()); }catch(e){} } else alert((res && res.data && res.data.message) || 'Failed');
                            }catch(err){
                              if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                                const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                                alert(msg);
                                localStorage.removeItem('token');
                                localStorage.removeItem('role');
                                return navigate('/superadmin/login');
                              }
                              alert(err.message || 'Failed');
                            }
                          }}>Unmark Home</button>
                        ) : (
                          <button className="btn" onClick={async ()=>{
                            if(!window.confirm('Set this news to home (featured)?')) return;
                            try{
                              const headers = token ? { Authorization: 'Bearer ' + token } : {};
                              const res = await api.put(`/api/news/admin/approved-news/${item._id}/feature`, null, { headers });
                              if (res && res.data && res.data.success) { alert('Marked as featured'); fetchApproved(); try{ window.dispatchEvent(new Event('featured-updated')); localStorage.setItem('featured-updated', Date.now().toString()); }catch(e){} } else alert((res && res.data && res.data.message) || 'Failed');
                            }catch(err){
                              if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                                const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                                alert(msg);
                                localStorage.removeItem('token');
                                localStorage.removeItem('role');
                                return navigate('/superadmin/login');
                              }
                              alert(err.message || 'Failed');
                            }
                          }}>Set To Home</button>
                        )}

                        <button className="btn danger" onClick={async ()=>{
                          if(!window.confirm('Delete this approved news?')) return;
                          try{
                            const headers = token ? { Authorization: 'Bearer ' + token } : {};
                            const res = await api.delete(`/api/news/admin/approved-news/${item._id}`, { headers });
                            if (res && res.data && res.data.success){ alert('Deleted'); fetchApproved(); try{ window.dispatchEvent(new Event('featured-updated')); localStorage.setItem('featured-updated', Date.now().toString()); }catch(e){} } else alert((res && res.data && res.data.message) || 'Failed');
                          }catch(err){
                            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                              const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                              alert(msg);
                              localStorage.removeItem('token');
                              localStorage.removeItem('role');
                              return navigate('/superadmin/login');
                            }
                            alert(err.message || 'Failed');
                          }
                        }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {view === 'public-approvals' && (
            <>
              <h3 className="section-title">Pending RR PUBLIC NEWS Approvals</h3>
              {items.length === 0 && !loading && <p className="muted">No pending public news</p>}
              <div className="approval-grid">
                {items.map(item => (
                  <div key={item._id} className="approval-card">
                    <div className="card-image">
                      <img src={imgFor(item)} alt={item.title} />
                    </div>
                    <div className="card-body">
                      <span className="news-category">{categoryBadge(item)}</span>
                      <div className="news-author">✍️ {item.uploaderName || item.author || '—'}</div>
                      <h3 className="news-title">{item.title}</h3>
                      <p className="news-description">{item.description}</p>
                      <div className="news-meta" style={{flexDirection:'column',alignItems:'flex-start',gap:4}}>
                        <span>Uploader Name: {item.uploaderName || item.author || '—'}</span>
                        <span>मोबाइल: {item.mobileNumber || '—'}</span>
                        <span>Aadhaar Number: {item.adharCardNumber || '—'}</span>
                        <span className="news-date">{new Date(item.createdAt).toLocaleString('hi-IN')}</span>
                      </div>
                      {(item.adharImageUrl || item.adharImagePath) && (
                        <div style={{marginTop:8}}>
                          <div style={{fontSize:12,fontWeight:700,marginBottom:4}}>Aadhaar Photo</div>
                          <img src={makeSrc(item.adharImageUrl || item.adharImagePath)} alt="Aadhaar" style={{width:120,height:72,objectFit:'cover',borderRadius:6,border:'1px solid #ddd'}} />
                        </div>
                      )}
                      <div className="card-actions">
                        <button className="btn view" onClick={()=> navigate(`/news/${item.slug}`, { state: { news: item, fromApproval: true } })}>View</button>
                        <button className="btn approve" onClick={()=>editPublic(item)}>Edit</button>
                        <button className="btn approve" onClick={()=>approvePublic(item._id)}>Approve</button>
                        <button className="btn danger" onClick={()=>deletePublic(item._id, false)}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {view === 'public-approved' && (
            <>
              <h3 className="section-title">RR PUBLIC NEWS (Approved)</h3>
              {items.length === 0 && !loading && <p className="muted">No approved public news</p>}
              <div className="approval-grid">
                {items.map(item => (
                  <div key={item._id} className="approval-card">
                    <div className="card-image">
                      <img src={imgFor(item)} alt={item.title} />
                    </div>
                    <div className="card-body">
                      <span className="news-category">{categoryBadge(item)}</span>
                      <div className="news-author">✍️ {item.uploaderName || item.author || '—'}</div>
                      <h3 className="news-title">{item.title}</h3>
                      <p className="news-description">{item.description}</p>
                      <div className="news-meta" style={{flexDirection:'column',alignItems:'flex-start',gap:4}}>
                        <span>Uploader Name: {item.uploaderName || item.author || '—'}</span>
                        <span>मोबाइल: {item.mobileNumber || '—'}</span>
                        <span>Aadhaar Number: {item.adharCardNumber || '—'}</span>
                        <span className="news-date">{new Date(item.createdAt).toLocaleString('hi-IN')}</span>
                      </div>
                      {(item.adharImageUrl || item.adharImagePath) && (
                        <div style={{marginTop:8}}>
                          <div style={{fontSize:12,fontWeight:700,marginBottom:4}}>Aadhaar Photo</div>
                          <img src={makeSrc(item.adharImageUrl || item.adharImagePath)} alt="Aadhaar" style={{width:120,height:72,objectFit:'cover',borderRadius:6,border:'1px solid #ddd'}} />
                        </div>
                      )}
                      <div className="card-actions">
                        <button className="btn view" onClick={()=> navigate(`/news/${item.slug}`, { state: { news: item, fromApproval: false } })}>View</button>
                        <button className="btn approve" onClick={()=>editPublic(item)}>Edit</button>
                        <button className="btn" onClick={()=>togglePublicHome(item)}>{item.isFeatured ? 'Unmark Home' : 'Set To Home'}</button>
                        <button className="btn danger" onClick={()=>deletePublic(item._id, true)}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {view === 'reporters' && (
            <div className="reporters-list">
              <h3 className="section-title">Reporter Accounts</h3>
              {reporters.length === 0 && !loading && <p className="muted">No reporters found</p>}
              <div className="contacts-grid">
                {reporters.map(r => (
                  <div key={r._id} className="contact-card">
                    <div className="contact-head">
                      <strong>{r.name}</strong>
                      <span className="contact-date">{new Date(r.createdAt).toLocaleString('hi-IN')}</span>
                    </div>
                    <div className="contact-body">
                      <p><strong>ईमेल:</strong> {r.email}</p>
                      <p><strong>स्थिति:</strong> {r.isApproved ? 'स्वीकृत' : 'पेंडिंग'}</p>
                      {r.pressRole && <p><strong>पद:</strong> {r.pressRole}</p>}
                      <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
                        {!r.isApproved && <button className="btn approve" onClick={async ()=>{
                          if(!window.confirm('Approve this reporter?')) return;
                          try{
                            const headers = token ? { Authorization: 'Bearer ' + token } : {};
                            const res = await api.put(`/api/users/reporters/${r._id}/approve`, null, { headers });
                            if (res && res.data && res.data.success){ alert('Reporter approved'); fetchReporters(); } else alert((res && res.data && res.data.message) || 'Failed');
                          }catch(err){
                            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                              const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                              alert(msg);
                              localStorage.removeItem('token');
                              localStorage.removeItem('role');
                              return navigate('/superadmin/login');
                            }
                            alert(err.message || 'Failed');
                          }
                        }}>Approve</button>}

                        <button className="btn" style={{background:'#4CAF50',color:'#fff'}} onClick={()=>{
                          setSelectedReporter(r);
                          setShowUploadModal(true);
                        }}>Upload Docs</button>

                        <button className="btn" style={{background:'#2196F3',color:'#fff'}} onClick={async ()=>{
                          setSelectedReporter(r);
                          setShowDocumentsModal(true);
                          await fetchDocuments(r._id);
                        }}>View Docs</button>

                        <button className="btn" style={{background:'#ddd',color:'#111'}} onClick={async ()=>{
                          if(!window.confirm('Delete this reporter?')) return;
                          try{
                            const headers = token ? { Authorization: 'Bearer ' + token } : {};
                            const res = await api.delete(`/api/users/reporters/${r._id}`, { headers });
                            if (res && res.data && res.data.success){ alert('Deleted'); fetchReporters(); } else alert((res && res.data && res.data.message) || 'Failed');
                          }catch(err){
                            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                              const msg = (err.response.data && err.response.data.message) || 'Unauthorized';
                              alert(msg);
                              localStorage.removeItem('token');
                              localStorage.removeItem('role');
                              return navigate('/superadmin/login');
                            }
                            alert(err.message || 'Failed');
                          }
                        }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'contacts' && (
            <div className="contacts-list">
              <h3 className="section-title">Contact Submissions</h3>
              {contacts.length === 0 && !loading && <p className="muted">No contacts yet</p>}
              <div className="contacts-grid">
                {contacts.map(c => (
                  <div key={c._id} className="contact-card">
                    <div className="contact-head">
                      <strong>{c.name}</strong>
                      <span className="contact-date">{new Date(c.createdAt).toLocaleString('hi-IN')}</span>
                    </div>
                    <div className="contact-body">
                      <p><strong>ईमेल:</strong> {c.email}</p>
                      <p><strong>मोबाइल:</strong> {c.mobile}</p>
                      {c.address && <p><strong>पता:</strong> {c.address}</p>}
                      <p className="contact-message">{c.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'consent' && (
            <div className="consent-forms-list">
              <h3 className="section-title">पत्रकार सहमति फॉर्म</h3>
              {consentForms.length === 0 && !loading && <p className="muted">कोई फॉर्म जमा नहीं किए गए</p>}
              <div className="consent-grid">
                {consentForms.map(form => (
                  <div key={form._id} className="consent-card">
                    <div className="consent-head">
                      <strong>{form.name}</strong>
                      <span className="consent-date">{form.consentData?.consentSubmittedAt ? new Date(form.consentData.consentSubmittedAt).toLocaleDateString('hi-IN') : 'N/A'}</span>
                    </div>
                    <div className="consent-body">
                      {form.consentData?.photo && (
                        <div style={{marginBottom:12,textAlign:'center'}}>
                          <img 
                            src={form.consentData.photo} 
                            alt="Reporter Photo" 
                            style={{maxWidth:'100%',maxHeight:100,borderRadius:4,border:'1px solid #ddd',objectFit:'contain'}} 
                          />
                        </div>
                      )}
                      <p><strong>पिता का नाम:</strong> {form.consentData?.fatherName || '-'}</p>
                      <p><strong>जन्म तिथि:</strong> {form.consentData?.dateOfBirth || '-'}</p>
                      <p><strong>लिंग:</strong> {form.consentData?.gender || '-'}</p>
                      <p><strong>वैवाहिक स्थिति:</strong> {form.consentData?.maritalStatus || '-'}</p>
                      <p><strong>रक्त समूह:</strong> {form.consentData?.bloodGroup || '-'}</p>
                      <p><strong>मोबाइल नंबर:</strong> {form.consentData?.mobileNumber || '-'}</p>
                      <p><strong>ई-मेल:</strong> {form.consentData?.email || '-'}</p>
                      <p><strong>भूमिका:</strong> {form.consentData?.reporterRole || '-'}</p>
                      <p><strong>योग्यता:</strong> {form.consentData?.qualification || '-'}</p>
                      <p><strong>व्यवसाय:</strong> {form.consentData?.profession || '-'}</p>
                      <p><strong>संस्था में नियुक्ति तिथि:</strong> {form.consentData?.appointmentDate || '-'}</p>
                      <p><strong>प्रेस कार्ड दिनांक:</strong> {form.consentData?.pressCardDate || '-'}</p>
                      {form.consentData?.signature && (
                        <div style={{marginTop:10,padding:10,border:'1px solid #ddd',borderRadius:4}}>
                          <img 
                            src={form.consentData.signature} 
                            alt="Signature" 
                            style={{maxWidth:'100%',maxHeight:100,objectFit:'contain'}} 
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'videos' && (
            <div className="videos-manager-view">
              <h3 className="section-title">Homepage Videos</h3>
              <AdminVideoManager token={token} />
            </div>
          )}

        </main>
      </div>
      
      {/* Upload Documents Modal */}
      {showUploadModal && selectedReporter && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: 500}}>
            <div className="modal-header">
              <h3>Upload Documents for {selectedReporter.name}</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom: 15, color: '#666'}}>
                Select document files to upload (PDF, DOC, DOCX, images, etc.)
              </p>
              <input 
                type="file" 
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    uploadDocument(file);
                    e.target.value = ''; // Reset input
                  }
                }}
                disabled={uploadingDoc}
                style={{
                  width: '100%',
                  padding: 10,
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  cursor: uploadingDoc ? 'not-allowed' : 'pointer'
                }}
              />
              {uploadingDoc && (
                <div style={{marginTop: 10, textAlign: 'center', color: '#666', fontSize: 14}}>
                  ⏳ Uploading...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`toast-notification toast-${toastType}`}>
          {toastType === 'success' && '✓ '}
          {toastType === 'error' && '✗ '}
          {toastMessage}
        </div>
      )}

      {/* View Documents Modal */}
      {showDocumentsModal && selectedReporter && (
        <div className="modal-overlay" onClick={() => setShowDocumentsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: 700}}>
            <div className="modal-header">
              <h3>Documents - {selectedReporter.name}</h3>
              <button className="modal-close" onClick={() => setShowDocumentsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {documents.length === 0 ? (
                <p style={{textAlign: 'center', color: '#999', padding: 20}}>
                  No documents uploaded yet
                </p>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                  {documents.map((doc) => (
                    <div 
                      key={doc._id} 
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 12,
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        background: '#f9f9f9'
                      }}
                    >
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 'bold', marginBottom: 4}}>{doc.name}</div>
                        <div style={{fontSize: 12, color: '#666'}}>
                          Uploaded: {new Date(doc.uploadedAt).toLocaleString('hi-IN')}
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: 8}}>
                        <a 
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn"
                          style={{
                            background: '#2196F3',
                            color: '#fff',
                            textDecoration: 'none',
                            padding: '6px 12px',
                            fontSize: 14
                          }}
                        >
                          Download
                        </a>
                        <button 
                          className="btn"
                          onClick={() => deleteDocument(doc._id)}
                          style={{
                            background: '#f44336',
                            color: '#fff',
                            padding: '6px 12px',
                            fontSize: 14
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {loading && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperadminApproval;

function MediaPreview({ src, alt, imgFallback }) {
  const [failed, setFailed] = React.useState(false);

  const makeSrc = (p) => {
    if (!p || p === 'null' || p === 'undefined') return '';
    if (/^https?:\/\//i.test(p) || p.startsWith('data:')) return p;
    const path = p.startsWith('/') ? p : '/' + p;
    return (API_BASE || '') + path;
  };

  const fallbackSrc = makeSrc(imgFallback) || '/placeholder.svg';
  if (failed) return <img src={fallbackSrc} alt={alt} />;

  const audioExt = /\.(mp3|wav|ogg|m4a|aac)$/i;
  const resolvedSrc = makeSrc(src);
  if (!resolvedSrc) return <img src={fallbackSrc} alt={alt} />;

  if (audioExt.test(resolvedSrc)) {
    return (
      <audio
        src={resolvedSrc}
        controls
        preload="metadata"
        onError={() => setFailed(true)}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <video
      src={resolvedSrc}
      controls
      playsInline
      preload="metadata"
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}


